import { useEffect } from "react";
import { Route, Routes } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { onUnauthorized } from "./api/client";
import Layout from "./components/Layout";
import { queryKeys } from "./hooks/queryKeys";
import { useAuthStatus } from "./hooks/useAuth";
import { useFirebaseUser } from "./hooks/useFirebaseUser";
import BookDetail from "./pages/BookDetail";
import Chat from "./pages/Chat";
import FirebaseLogin from "./pages/FirebaseLogin";
import Library from "./pages/Library";
import Login from "./pages/Login";
import NotInvited from "./pages/NotInvited";
import Settings from "./pages/Settings";
import type { AuthStatus } from "./api/types";

export default function App() {
  const queryClient = useQueryClient();
  const { data: authStatus, isLoading } = useAuthStatus();
  const firebaseMode = authStatus?.mode === "firebase";
  // undefined while Firebase restores the session, then User or null.
  const firebaseUser = useFirebaseUser(firebaseMode);

  // Any API call anywhere in the app that gets a 401 flips us into the login
  // screen (§7 / §5 auth). Merge, so the auth mode is not clobbered.
  useEffect(() => {
    return onUnauthorized(() => {
      queryClient.setQueryData(
        queryKeys.authStatus(),
        (old: AuthStatus | undefined): AuthStatus => ({
          mode: "password",
          email: null,
          invited: true,
          ...old,
          required: true,
          authenticated: false,
        })
      );
    });
  }, [queryClient]);

  // The auth status was fetched before sign-in; re-ask now that requests
  // carry a token, so the backend's verdict (including the allowlist) is known.
  useEffect(() => {
    if (firebaseMode && firebaseUser) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.authStatus() });
    }
  }, [firebaseMode, firebaseUser, queryClient]);

  if (isLoading || (firebaseMode && firebaseUser === undefined)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg">
        <p className="animate-pulse-soft font-serif text-lg text-muted">Boort</p>
      </div>
    );
  }

  if (firebaseMode) {
    if (!firebaseUser) return <FirebaseLogin />;
    if (authStatus && !authStatus.invited) return <NotInvited email={authStatus.email} />;
  } else if (authStatus?.required && !authStatus.authenticated) {
    return <Login />;
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Library />} />
        <Route path="/books/:id" element={<BookDetail />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
    </Routes>
  );
}
